/**
 * Tipos e campos do cadastro de ativos e da ficha técnica — Nexora Grid (frontend, US-406).
 *
 * Criado em: 16/10/2026 · Última modificação: 25/10/2026 · Versão: 1.2.0
 *
 * Espelha `ativos/modelo.py` e `ativos/ficha.py` (GUS-1101/1102). A validação que vale é a do backend: aqui ficam
 * só os rótulos, as unidades e o tipo de cada campo para montar o formulário.
 * v1.1 (retorno de 18/10/2026): elevatória com a ficha da bomba; instalação de superfície ou submersa; seção do poço
 * (NBR 12212/12244) que aparece para a bomba submersa e o poço profundo.
 */

export type DadosAtivo = {
  identificacao: string; tipo: string; nome: string; latitude: number; longitude: number; cota_m: number | null;
  setor: string; unidade: string; device_id: string | null; registro_fechado: boolean | null;
};
export type Ativo = {
  id: string; empresa: string; situacao: "pendente" | "validado" | "rejeitado"; origem: string; versao: number;
  cota_origem: string | null; motivo_situacao: string; criado_por: string; criado_em: string; atualizado_em: string;
  dados: DadosAtivo; ficha_versao: number; ficha: Record<string, unknown>; pendencias_ficha: string[];
};
export type Ficha = { ativo_id: string; tipo: string; versao: number; ficha: Record<string, unknown>;
  campos_minimos: string[]; pendencias: string[]; completa: boolean; submersa?: boolean; avisos?: string[] };

export const TIPOS: Record<string, string> = {
  bomba: "Bomba", poco_profundo: "Poço profundo (bomba submersa)", reservatorio: "Reservatório",
  macromedidor: "Macromedidor", vrp: "VRP", registro: "Registro", elevatoria: "Estação elevatória",
  ventosa: "Ventosa", hidrante: "Hidrante", ponto_pressao: "Ponto de pressão",
};
export const SITUACOES: Record<string, string> = { pendente: "Pendente", validado: "Validado", rejeitado: "Rejeitado" };
export const ORIGENS: Record<string, string> = { engenharia: "Engenharia", importacao: "Importação DWG", campo: "Campo" };

export type Campo = {
  nome: string; rotulo: string; unidade?: string; tipo: "numero" | "texto" | "opcao" | "data" | "curva";
  opcoes?: [string, string][]; colunas?: [string, string][];
  /** título de seção mostrado antes do campo */
  secao?: string;
  /** só aparece para a bomba submersa (e o poço profundo) */
  submersa?: boolean;
};

const OBS: Campo = { nome: "observacao", rotulo: "Observação", tipo: "texto" };
const DN: Campo = { nome: "dn_mm", rotulo: "Diâmetro nominal", unidade: "mm", tipo: "numero" };
const BOMBA: Campo[] = [
  { nome: "estacao", rotulo: "Estação / unidade", tipo: "texto", secao: "Instalação" },
  { nome: "instalacao", rotulo: "Instalação da bomba", tipo: "opcao",
    opcoes: [["superficie", "Superfície (no abrigo)"], ["submersa", "Submersa (no poço, abaixo do cavalete)"]] },
  { nome: "tensao_v", rotulo: "Tensão", unidade: "V", tipo: "numero", secao: "Motor e bomba" },
  { nome: "corrente_nominal_a", rotulo: "Corrente nominal", unidade: "A", tipo: "numero" },
  { nome: "potencia_kw", rotulo: "Potência", unidade: "kW", tipo: "numero" },
  { nome: "ligacao", rotulo: "Ligação do motor", tipo: "opcao", opcoes: [["estrela", "Estrela"], ["triangulo", "Triângulo"]] },
  { nome: "partida", rotulo: "Partida", tipo: "opcao", opcoes: [["direta", "Direta"], ["estrela_triangulo", "Estrela-triângulo"],
    ["soft_starter", "Soft-starter"], ["inversor", "Inversor de frequência"], ["compensadora", "Compensadora"]] },
  { nome: "curva", rotulo: "Curva da bomba (vazão × altura)", tipo: "curva",
    colunas: [["vazao_m3h", "Vazão (m³/h)"], ["altura_m", "Altura (m)"]] },
  { nome: "rotacao_rpm", rotulo: "Rotação", unidade: "rpm", tipo: "numero" },
  { nome: "rendimento", rotulo: "Curva de rendimento", tipo: "curva", colunas: [["vazao_m3h", "Vazão (m³/h)"], ["rendimento_pct", "Rendimento (%)"]] },
  { nome: "npsh_requerido_m", rotulo: "NPSH requerido", unidade: "m", tipo: "numero" },
  { nome: "marca", rotulo: "Marca", tipo: "texto" },
  { nome: "modelo", rotulo: "Modelo", tipo: "texto" },
  { nome: "ano", rotulo: "Ano", tipo: "numero" },
  { nome: "cota_instalacao_m", rotulo: "Cota de instalação", unidade: "m", tipo: "numero" },
  { nome: "frequencia_nominal_hz", rotulo: "Frequência nominal do inversor", unidade: "Hz", tipo: "numero" },
  // poço tubular e bomba submersa (ABNT NBR 12212 e NBR 12244) — profundidades a partir da boca do poço
  { nome: "profundidade_poco_m", rotulo: "Profundidade do poço", unidade: "m", tipo: "numero", submersa: true,
    secao: "Poço e bomba submersa (NBR 12212 / NBR 12244) — profundidades a partir da boca do poço" },
  { nome: "nivel_estatico_m", rotulo: "Nível estático (NE, bomba parada)", unidade: "m", tipo: "numero", submersa: true },
  { nome: "nivel_dinamico_m", rotulo: "Nível dinâmico (ND, bombeando)", unidade: "m", tipo: "numero", submersa: true },
  { nome: "profundidade_instalacao_m", rotulo: "Profundidade de instalação da bomba (crivo)", unidade: "m", tipo: "numero", submersa: true },
  { nome: "topo_filtros_m", rotulo: "Topo da seção filtrante", unidade: "m", tipo: "numero", submersa: true },
  { nome: "diametro_revestimento_mm", rotulo: "Diâmetro do revestimento", unidade: "mm", tipo: "numero", submersa: true },
  { nome: "vazao_teste_m3h", rotulo: "Vazão do teste de bombeamento", unidade: "m³/h", tipo: "numero", submersa: true },
  { nome: "data_teste_bombeamento", rotulo: "Data do teste de bombeamento", tipo: "data", submersa: true },
  { nome: "vazao_outorgada_m3h", rotulo: "Vazão outorgada", unidade: "m³/h", tipo: "numero", submersa: true },
  { nome: "outorga", rotulo: "Número da outorga", tipo: "texto", submersa: true },
  { nome: "diametro_edutor_mm", rotulo: "Diâmetro da tubulação edutora", unidade: "mm", tipo: "numero", submersa: true },
  { nome: "estagios", rotulo: "Estágios da bomba", tipo: "numero", submersa: true },
  { nome: "diametro_bomba_pol", rotulo: "Diâmetro nominal da bomba", unidade: "pol", tipo: "numero", submersa: true },
  { ...OBS, secao: "Observação" },
];

export const CAMPOS_FICHA: Record<string, Campo[]> = {
  bomba: BOMBA,
  poco_profundo: BOMBA,
  reservatorio: [
    { nome: "tipo_construtivo", rotulo: "Tipo construtivo", tipo: "opcao",
      opcoes: [["apoiado", "Apoiado"], ["elevado", "Elevado"], ["semienterrado", "Semienterrado"]] },
    { nome: "volume_m3", rotulo: "Volume", unidade: "m³", tipo: "numero" },
    { nome: "cota_fundo_m", rotulo: "Cota de fundo", unidade: "m", tipo: "numero" },
    { nome: "nivel_min_m", rotulo: "Nível mínimo de operação", unidade: "m", tipo: "numero" },
    { nome: "nivel_max_m", rotulo: "Nível máximo de operação", unidade: "m", tipo: "numero" },
    { nome: "curva_volume", rotulo: "Curva de volume (nível × volume)", tipo: "curva",
      colunas: [["nivel_m", "Nível (m)"], ["volume_m3", "Volume (m³)"]] },
    { nome: "diametro_m", rotulo: "Diâmetro", unidade: "m", tipo: "numero" },
    { nome: "altura_lamina_m", rotulo: "Altura da lâmina", unidade: "m", tipo: "numero" },
    OBS,
  ],
  macromedidor: [DN, { nome: "trecho", rotulo: "Trecho / adutora", tipo: "texto" },
    { nome: "tipo_medidor", rotulo: "Tipo", tipo: "opcao", opcoes: [["eletromagnetico", "Eletromagnético"],
      ["ultrassonico", "Ultrassônico"], ["mecanico", "Mecânico"]] },
    { nome: "faixa_min_m3h", rotulo: "Faixa mínima", unidade: "m³/h", tipo: "numero" },
    { nome: "faixa_max_m3h", rotulo: "Faixa máxima", unidade: "m³/h", tipo: "numero" },
    { nome: "data_calibracao", rotulo: "Data de calibração", tipo: "data" }, OBS],
  vrp: [DN, { nome: "setpoint_jusante_mca", rotulo: "Pressão de ajuste a jusante (setpoint)", unidade: "mca", tipo: "numero" },
    { nome: "faixa_piloto_min_mca", rotulo: "Faixa do piloto — mínima", unidade: "mca", tipo: "numero" },
    { nome: "faixa_piloto_max_mca", rotulo: "Faixa do piloto — máxima", unidade: "mca", tipo: "numero" },
    { nome: "perda_carga_mca", rotulo: "Perda de carga", unidade: "mca", tipo: "numero" }, OBS],
  registro: [DN, OBS],
  elevatoria: BOMBA,
  ventosa: [DN, OBS],
  hidrante: [DN, OBS],
  ponto_pressao: [DN, OBS],
};

export const fmtData = (iso: string) => new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
